import { Box, Button, ButtonProps, Divider, Menu } from "@mantine/core"
import { type MenuProps } from "@mantine/core"
import { IconChevronDown } from "@tabler/icons-react"

interface TButtonWithMenuProps {
  onClick?: () => void
  buttonProps?: ButtonProps
  menuProps?: MenuProps
  menuDropdownItems?: React.ReactNode[]
  children?: React.ReactNode
}

/**
 * Renders a button with a dropdown menu.
 * IMPORTANT: In every Menu.Item that you add in the menuDropdownItems prop, you must add add inside the onClick handler some code to stop the propagation of the click event. This is necessary to prevent the parent button from being clicked when clicking on a menu item.
 * For example:
 * ```tsx
 * <Menu.Item onClick={(e) => { e.stopPropagation(); console.log("Clicked on menu item") }}>Menu item</Menu.Item>
 * ```
 *
 * @param onClick - Function to be called when the button is clicked.
 * @param buttonProps - Props for the button component.
 * @param menuProps - Props for the menu component.
 * @param menuDropdownItems - Items to be displayed in the dropdown menu.
 * @param children - The content to be rendered inside the button.
 * @returns The rendered button with a dropdown menu.
 */
export function ButtonWithMenu({
  onClick,
  buttonProps,
  menuProps,
  menuDropdownItems,
  children,
}: TButtonWithMenuProps) {
  return (
    <Button
      onClick={onClick}
      size="sm"
      radius="sm"
      variant="filled"
      color="rBackBlue.9"
      {...buttonProps}
      rightSection={
        <Menu shadow="md" {...menuProps}>
          <Menu.Target>
            <Box
              className="flex flex-row items-center justify-end gap-4"
              onClick={(e) => e.stopPropagation()}
            >
              <Divider size="sm" orientation="vertical" />
              <IconChevronDown className="size-4" />
            </Box>
          </Menu.Target>

          <Menu.Dropdown>{menuDropdownItems}</Menu.Dropdown>
        </Menu>
      }
    >
      {children}
    </Button>
  )
}
